import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import {
  getDocs,
  serverTimestamp,
  doc,
  onSnapshot,
  setDoc,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { db } from "../firebaseConfig";
import Nav from "./Nav";
import Sidebar from "./Sidebar";
import { useNavContext } from "../context/NavContext";
import Generador from "./Generador";
import VendedorModal from "./VendedorModal";
import moment from "moment";
import DatePicker, { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import "react-datepicker/dist/react-datepicker.css";
import { useNumeroConfig } from "../hooks/useNumeroConfig";
import { useAuth } from "../context/AuthContext";
import { getSubCollection } from "../utils/getSubCollection";
import useArchivos from "../hooks/useArchivos";
import useProductos from "../hooks/useProductos";

moment.locale("es");
registerLocale("es", es);

const GeneradorModal = ({
  editingReport,
  reportes,
  onClose = () => {},
  cloneMode = false,
}) => {
  const { user, teamOwnerId } = useAuth();
  const ownerUid = teamOwnerId || user.uid;
  const {
    numero: numeroObraAuto,
    modo: modoNumeroObra,
    abrirConfiguracion,
    NumeroConfigModalUI,
  } = useNumeroConfig("numeroObra", {
    title: "Configurar número de obra",
  });
  const [draftId, setDraftId] = useState(uuidv4());
  const isEdit = Boolean(editingReport?.id) && !cloneMode;
  const parentId = isEdit /* edición real */
    ? editingReport.id
    : draftId; /* tanto nuevo como cloneMode usarán draftId */
  const {
    archivos,
    cargando,
    manejarArchivos,
    eliminarArchivo,
    descargarArchivo,
    obtenerIcono,
  } = useArchivos("obras", parentId);
  const [productos, setProductos] = useState([]);
  const {
    datos,
    descuento,
    añadirFila,
    eliminar,
    actualizarCantidad,
    handleInputChange,
    activeRow,
    iva,
    total,
    filteredProductos,
    setFilteredProductos,
    obtenerIVA,
    calcularSubtotal,
    handleFileChange,
    setDatos,
    setDescuento,
    agregarBulk,
  } = useProductos(
    editingReport?.productosSeleccionados || [],
    editingReport?.descuento || 0,
    productos
  );
  const { proximasAVencer, proximosEventos } = useNavContext();
  const [cliente, setCliente] = useState("");
  const [clientes, setClientes] = useState([]);
  const [asunto, setAsunto] = useState("");
  const [fechaReporte, setFechaReporte] = useState(new Date());
  const [usuario, setUsuario] = useState("");
  const [planta, setPlanta] = useState("");
  const [divisa, setDivisa] = useState("MXN");
  const [area, setArea] = useState("");
  const [referencia, setReferencia] = useState("");
  const [ordenDeCompra, setOrdenDeCompra] = useState("");
  const [terminos, setTerminos] = useState("");
  const [notas, setNotas] = useState("");
  const [vendedores, setVendedores] = useState([]);
  const [vendedorNombre, setVendedorNombre] = useState("");
  const [vendedorCorreo, setVendedorCorreo] = useState("");
  const [numeroSobrescrito, setNumeroSobrescrito] = useState(null);
  const [modalVendedorIsOpen, setModalVendedorIsOpen] = useState(false);
  const [mostrarTabla] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [formDirty, setFormDirty] = useState(false);

  useEffect(() => {
    // 1) Si vengo en clonación, quiero que draftId sea el id del clon
    if (cloneMode && editingReport) {
      setDraftId(editingReport.id);
    }
    // 2) Si no hay reporte cargado (nuevo), generamos un draftId nuevo
    else if (!editingReport) {
      setDraftId(uuidv4());
    }

    if (editingReport && !cloneMode) {
      // EDICIÓN de un reporte EXISTENTE
      setModoEdicion(true);
      setNumeroSobrescrito(
        editingReport.numeroObra ?? editingReport.numeroReporte ?? ""
      );

      /* --- resto de campos desde editingReport --- */
      setCliente(editingReport.cliente || "");
      setReferencia(editingReport.referencia || "");
      setFechaReporte(
        editingReport.fechaReporte
          ? new Date(editingReport.fechaReporte)
          : new Date()
      );
      setOrdenDeCompra(editingReport.ordenDeCompra || "");
      setUsuario(editingReport.usuario || "");
      setPlanta(editingReport.planta || "");
      setDivisa(editingReport.divisa || "MXN");
      setArea(editingReport.area || "");
      setAsunto(editingReport.asunto || "");
      setTerminos(editingReport.terminos || "");
      setNotas(editingReport.notas || "");
      setVendedorNombre(editingReport.vendedorNombre || "");
      setVendedorCorreo(editingReport.vendedorCorreo || "");

      /* productos */
      const prods = editingReport.productosSeleccionados || [];
      setDatos(
        prods.map((prod) => ({
          id: uuidv4(),
          ...prod,
          descripcion: prod.descripcion || prod.nombre,
          numeroDeParteCliente: prod.numeroDeParteCliente || "",
          cantidad: prod.cantidad ?? 1,
          tarifa: prod.tarifa ?? 0,
          iva: prod.iva ?? 0,
          descuento: prod.descuento ?? 0,
          tipoDescuento: prod.tipoDescuento || "%",
          importe: prod.importe ?? 0,
          unidad: prod.unidad || "",
          cantidadInstalada: prod.cantidadInstalada || 0,
          avance: prod.avance || 0,
          imagenUrl: prod.imagenUrl || "",
        }))
      );
      setDescuento(parseFloat(editingReport.descuento) || 0);
    } else if (editingReport && cloneMode) {
      // CLONAR un reporte (modo “nuevo”, pero con datos precargados)
      setModoEdicion(false);
      setNumeroSobrescrito(
        editingReport.numeroObra ?? editingReport.numeroReporte ?? ""
      );

      /* misma inicialización de campos que en edición */
      setCliente(editingReport.cliente || "");
      setReferencia(editingReport.referencia || "");
      setFechaReporte(
        editingReport.fechaReporte
          ? new Date(editingReport.fechaReporte)
          : new Date()
      );
      setOrdenDeCompra(editingReport.ordenDeCompra || "");
      setUsuario(editingReport.usuario || "");
      setPlanta(editingReport.planta || "");
      setDivisa(editingReport.divisa || "MXN");
      setArea(editingReport.area || "");
      setAsunto(editingReport.asunto || "");
      setTerminos(editingReport.terminos || "");
      setNotas(editingReport.notas || "");
      setVendedorNombre(editingReport.vendedorNombre || "");
      setVendedorCorreo(editingReport.vendedorCorreo || "");

      /* clonar productos */
      const cloneProds = editingReport.productosSeleccionados || [];
      setDatos(
        cloneProds.map((prod) => ({
          id: uuidv4(),
          ...prod,
          descripcion: prod.descripcion || prod.nombre,
          numeroDeParteCliente: prod.numeroDeParteCliente || "",
          cantidad: prod.cantidad ?? 1,
          tarifa: prod.tarifa ?? 0,
          iva: prod.iva ?? 0,
          descuento: prod.descuento ?? 0,
          tipoDescuento: prod.tipoDescuento || "%",
          importe: prod.importe ?? 0,
          unidad: prod.unidad || "",
          cantidadInstalada: prod.cantidadInstalada || 0,
          avance: prod.avance || 0,
          imagenUrl: prod.imagenUrl || "",
        }))
      );
      setDescuento(parseFloat(editingReport.descuento) || 0);
    } else {
      // Nuevo reporte completamente en blanco
      setModoEdicion(false);
      setNumeroSobrescrito(null);

      setCliente("");
      setReferencia("");
      setFechaReporte(new Date());
      setOrdenDeCompra("");
      setUsuario("");
      setPlanta("");
      setDivisa("MXN");
      setArea("");
      setAsunto("");
      setTerminos("");
      setNotas("");
      setVendedorNombre("");
      setVendedorCorreo("");

      setDatos([
        {
          id: uuidv4(),
          nombre: "",
          descripcion: "",
          numeroDeParteCliente: "",
          unidad: "",
          cantidad: 1,
          tarifa: 0,
          iva: 0.16,
          descuento: 0,
          tipoDescuento: "%",
          importe: 0,
          cantidadInstalada: 0,
          avance: 0,
          imagenUrl: "",
        },
      ]);
      setDescuento(0);
    }
  }, [editingReport, cloneMode, setDatos, setDescuento]);

  useEffect(() => {
    // 1) Suscripción en tiempo real a vendedores:
    const vendedoresCol = getSubCollection(ownerUid, "vendedores");
    const unsubscribe = onSnapshot(
      vendedoresCol,
      (snap) =>
        setVendedores(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Error al suscribir vendedores:", err)
    );

    // 2) Carga inicial de clientes y productosClientes:
    (async () => {
      try {
        // ---- clientes ----
        const clientesSnap = await getDocs(
          getSubCollection(ownerUid, "clientes")
        );
        const clientesList = clientesSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setClientes(
          clientesList.filter(
            (c, i, arr) => arr.findIndex((x) => x.empresa === c.empresa) === i
          )
        );

        // ---- productosClientes ----
        const productosSnap = await getDocs(
          getSubCollection(ownerUid, "productosClientes")
        );
        const productosList = productosSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          tipoDescuento: "cantidad",
          valorDescuento: 0,
        }));
        setProductos(productosList);
      } catch (error) {
        console.error("Error al cargar clientes o productosClientes:", error);
      }
    })();

    return () => unsubscribe();
  }, [ownerUid]);

  const openModalVendedor = () => {
    setModalVendedorIsOpen(true);
  };

  const closeModalVendedor = () => {
    setModalVendedorIsOpen(false);
  };

const handleCancel = () => {
  if (formDirty) {
    Swal.fire({
      title: "¿Seguro que quieres salir?",
      text: "Se perderán los cambios realizados",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, salir",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        onClose(); // ← Correcto
        setFormDirty(false);
      }
    });
  } else {
    onClose(); // ← Correcto
  }
};


  const guardar = async (e) => {
    e.preventDefault();

    /* ───────── 1) VALIDACIÓN ───────── */
    const numeroEfectivo = numeroSobrescrito ?? numeroObraAuto;
    if (!cliente || !numeroEfectivo || !fechaReporte || !ordenDeCompra) {
      await Swal.fire({
        icon: "error",
        title: "Campos obligatorios faltantes",
        text: "Completa: Cliente, No. Reporte, Fecha y Orden de compra.",
      });
      return;
    }

    try {
      /* ───────── 2) DUPLICADOS ───────── */
      const obrasCol = getSubCollection(ownerUid, "obras");
      const snap = await getDocs(obrasCol);
      const dup = snap.docs.find((d) => d.data().numeroObra === numeroEfectivo);

      if (dup && (!modoEdicion || dup.id !== editingReport?.id)) {
        await Swal.fire({
          icon: "error",
          title: "Número duplicado",
          text: `Ya existe el reporte “${numeroEfectivo}”.`,
        });
        return;
      }

      /* ───────── 3) PAYLOAD ───────── */
      const clienteSel = clientes.find((c) => c.id === cliente) || {};
      const data = {
        cliente,
        nombreCliente: clienteSel.empresa || "",
        numeroObra: numeroEfectivo,
        referencia,
        ordenDeCompra,
        fechaReporte: fechaReporte.toISOString(),
        usuario,
        vendedorNombre,
        vendedorCorreo,
        planta,
        area,
        divisa,
        asunto,
        productosSeleccionados: datos,
        descuento,
        total,
        terminos,
        notas,
        archivos,
        estado: "Pendiente",
        ...(modoEdicion
          ? { updatedAt: serverTimestamp() }
          : { createdAt: serverTimestamp() }),
      };

      /* ───────── 4) CREACIÓN / EDICIÓN ───────── */
      if (modoEdicion && editingReport?.id) {
        await setDoc(doc(obrasCol, editingReport.id), data, { merge: true });
      } else {
        await setDoc(doc(obrasCol, draftId), data);

        // — auto-incremento del folio —
        if (modoNumeroObra === "auto") {
          const m = numeroEfectivo.match(/^(.*?)(\d+)$/);
          if (m) {
            const [, prefijo = "", raw] = m;
            await setDoc(
              doc(db, "usuarios", ownerUid, "configuracion", "numeroObra"),
              { modo: "auto", prefijo, siguienteNumero: parseInt(raw, 10) + 1 },
              { merge: true }
            );
          }
        }
      }

      /* ───────── 5) ÉXITO ───────── */
      setFormDirty(false); // evita el aviso “cambios sin guardar”

      onClose(); // ⬅️ CIERRA el modal YA — antes del toast

      Swal.fire({
        icon: "success",
        title: modoEdicion ? "Reporte actualizado" : "Reporte guardado",
        timer: 1500,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      await Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text: err.message,
      });
    }
  };

  return (
    <>
      <Nav
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      <Sidebar />

      <div className="cotizacion-form">
        <form
          onKeyDown={(e) => {
            // Solo bloquear "Enter" fuera de los elementos <textarea>
            if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
              e.preventDefault();
            }
          }}
        >
          <div>
            <div className="group apartado">
              <label htmlFor="cliente" className="required">
                Cliente*
              </label>
              <select
                id="cliente"
                name="cliente"
                value={cliente}
                onChange={(e) => {
                  setCliente(e.target.value);
                  setFormDirty(true); // Marcar que el formulario ha sido modificado
                }}
              >
                <option value="">Seleccionar cliente</option>
                {clientes
                  .slice() // Crea una copia para evitar modificar el estado original
                  .sort((a, b) => a.empresa.localeCompare(b.empresa)) // Ordenar alfabéticamente
                  .map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.empresa}
                    </option>
                  ))}
              </select>
            </div>

            <br />

            <div className="group">
              <label htmlFor="numeroObra" className="required">
                No. Reporte#*
              </label>
              <div className="input-wrapper input-wrapper-expanded">
                <input
                  type="text"
                  id="numeroObra"
                  name="numeroObra"
                  // Si hay un valor manual (override), lo mostramos; si no, el automático
                  value={numeroSobrescrito ?? numeroObraAuto}
                  onChange={(e) => {
                    // Solo permitimos override en modo manual
                    if (modoNumeroObra === "manual") {
                      setNumeroSobrescrito(e.target.value);
                      setFormDirty(true);
                    }
                  }}
                  disabled={modoEdicion || modoNumeroObra === "auto"}
                />
                <button
                  type="button"
                  className="icon-button"
                  onClick={abrirConfiguracion}
                >
                  <i className="bi bi-gear-fill"></i>
                </button>
              </div>
            </div>

            <div className="group fechas">
              <div className="group">
                <label htmlFor="referencia">Referencia</label>
                <input
                  type="text"
                  id="referencia"
                  name="referencia"
                  value={referencia}
                  onChange={(e) => {
                    setReferencia(e.target.value);
                    setFormDirty(true); // Marcar que el formulario ha sido modificado
                  }}
                />
              </div>

              <div className="group">
                <label htmlFor="ordenDeCompra" className="required">
                  Orden de Compra*
                </label>
                <input
                  type="text"
                  id="ordenDeCompra"
                  name="ordenDeCompra"
                  value={ordenDeCompra}
                  onChange={(e) => {
                    setOrdenDeCompra(e.target.value);
                    setFormDirty(true); // Marcar que el formulario ha sido modificado
                  }}
                />
              </div>
            </div>

            <div className="group fechas">
              <div className="group">
                <label htmlFor="fecha-reporte" className="required">
                  Fecha de Reporte*
                </label>
                <DatePicker
                  selected={fechaReporte}
                  onChange={(date) => {
                    setFechaReporte(date);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                  dateFormat="dd MMM yyyy"
                  placeholderText="dd MMM yyyy"
                  id="fecha-cotizacion"
                  locale="es"
                />
              </div>
            </div>

            <hr
              style={{
                marginBottom: "30px",
                marginTop: "20px",
                border: "1px solid #f9f9fb",
              }}
            />

            <div className="group fechas">
              <div className="group">
                <label htmlFor="vendedorNombre">Vendedor</label>
                <select
                  style={{ width: "42%" }}
                  id="vendedorNombre"
                  value={
                    vendedores.find((v) => v.nombre === vendedorNombre)?.id ||
                    ""
                  }
                  onChange={(e) => {
                    const sel = e.target.value;
                    if (sel === "agregar-nuevo") openModalVendedor();
                    else {
                      const v = vendedores.find((v) => v.id === sel);
                      setVendedorNombre(v?.nombre || "");
                      setVendedorCorreo(v?.correo || "");
                    }
                    setFormDirty(true);
                  }}
                >
                  <option value="">Seleccionar vendedor</option>
                  {vendedores.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.nombre}
                    </option>
                  ))}
                  <option value="agregar-nuevo">Agregar nuevo vendedor</option>
                </select>

                <VendedorModal
                  isOpen={modalVendedorIsOpen}
                  onClose={closeModalVendedor}
                  initialNombre={vendedorNombre}
                  initialCorreo={vendedorCorreo}
                  onSelect={(nuevo) => {
                    // callback que dispara VendedorModal al guardar/seleccionar
                    setVendedorNombre(nuevo.nombre);
                    setVendedorCorreo(nuevo.correo);
                    setFormDirty(true);
                  }}
                />
              </div>

              <div className="group">
                <label htmlFor="usuario">Usuario</label>
                <input
                  type="text"
                  id="usuario"
                  name="usuario"
                  value={usuario}
                  onChange={(e) => {
                    setUsuario(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                />
              </div>
            </div>

            <div className="group">
              <label htmlFor="planta">Planta</label>
              <input
                type="text"
                id="planta"
                name="planta"
                value={planta}
                onChange={(e) => {
                  setPlanta(e.target.value);
                  setFormDirty(true); // Marcar el formulario como modificado
                }}
              />
            </div>

            <div className="group fechas">
              <div className="group">
                <label htmlFor="area">Área</label>
                <input
                  type="text"
                  id="area"
                  name="area"
                  value={area}
                  onChange={(e) => {
                    setArea(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                />
              </div>
              <div className="group">
                <label htmlFor="divisa">Divisa</label>
                <select
                  id="divisa"
                  name="divisa"
                  value={divisa}
                  onChange={(e) => {
                    setDivisa(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                >
                  <option value="MXN">MXN</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>

            <br />

            <div className="group">
              <label htmlFor="asunto">Asunto</label>
              <input
                type="text"
                id="asunto"
                name="asunto"
                value={asunto}
                onChange={(e) => {
                  setAsunto(e.target.value);
                  setFormDirty(true); // Marcar el formulario como modificado
                }}
              />
            </div>

            <br />

            <div className="apartado2">Tabla de productos</div>

            <div>
              {/* Contenedor para la tabla y los botones */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  width: "95%",
                }}
              >
                {/* Tabla general */}
                <div>
                  <table className="productos-table">
                    <thead>
                      <tr>
                        <th>Detalles del Artículo</th>
                        <th style={{ textAlign: "center" }}>Modelo</th>
                        <th id="unidad-col">UNIDAD</th>
                        <th id="cantidad-col">Cantidad</th>
                        <th id="precio-col">Precio</th>
                        <th id="importe-col">Importe</th>
                        <th id="instalada-col">Cant. instalada</th>
                        <th id="avance-col">Avance %</th>
                        <th id="imagen-col">Imagen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.map((producto) => (
                        <tr key={producto.id} style={{ position: "relative" }}>
                          {/* Detalles del Artículo */}
                          <td>
                            <div style={{ position: "relative" }}>
                              <textarea
                                className="list-products"
                                value={
                                  producto.descripcion || producto.nombre || ""
                                }
                                onChange={(e) => {
                                  handleInputChange(e, producto.id);
                                  setFormDirty(true);
                                }}
                                onBlur={() =>
                                  setTimeout(
                                    () => setFilteredProductos([]),
                                    100
                                  )
                                }
                                placeholder="Escriba para seleccionar un artículo"
                                style={{
                                  width: "400px",
                                  height: "130px",
                                  padding: "8px",
                                  fontSize: "14px",
                                  lineHeight: "1.5",
                                  resize: "vertical",
                                  overflow: "auto",
                                  whiteSpace: "pre-wrap",
                                  fontFamily: "Arial, sans-serif",
                                }}
                              />
                              {activeRow === producto.id &&
                                filteredProductos.length > 0 && (
                                  <ul
                                    style={{
                                      position: "absolute",
                                      top: "105%",
                                      left: 0,
                                      width: "100%",
                                      backgroundColor: "#fff",
                                      border: "1px solid #cecece",
                                      borderRadius: "5px",
                                      zIndex: 10,
                                      maxHeight: "150px",
                                      overflowY: "auto",
                                      listStyle: "none",
                                      padding: "5px",
                                      margin: 0,
                                    }}
                                  >
                                    {filteredProductos.map((prod) => (
                                      <li
                                        key={prod.id}
                                        onMouseDown={() => {
                                          handleInputChange(
                                            { target: { value: prod.nombre } },
                                            producto.id
                                          );
                                          setFormDirty(true);
                                        }}
                                        style={{
                                          padding: "5px",
                                          cursor: "pointer",
                                          backgroundColor: "#f9f9f9",
                                          marginBottom: "2px",
                                        }}
                                      >
                                        {prod.nombre}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                            </div>
                          </td>

                          {/* Modelo */}
                          <td>
                            <input
                              type="text"
                              value={
                                producto.modelo ||
                                producto.numeroDeParteCliente ||
                                ""
                              }
                              onChange={(e) => {
                                const modelo = e.target.value;
                                setDatos((prev) =>
                                  prev.map((item) =>
                                    item.id === producto.id
                                      ? { ...item, modelo }
                                      : item
                                  )
                                );
                                setFormDirty(true);
                              }}
                            />
                          </td>

                          {/* Unidad */}
                          <td>
                            <select
                              value={producto.unidad || ""}
                              onChange={(e) => {
                                const unidad = e.target.value;
                                setDatos((prev) =>
                                  prev.map((item) =>
                                    item.id === producto.id
                                      ? { ...item, unidad }
                                      : item
                                  )
                                );
                                setFormDirty(true);
                              }}
                            >
                              <option value="">Seleccionar</option>
                              <option value="pieza">Pieza</option>
                              <option value="servicio">Servicio</option>
                              <option value="kit">Kit</option>
                              <option value="metro">Metro</option>
                            </select>
                          </td>

                          {/* Cantidad */}
                          <td id="cantidad-col">
                            <input
                              type="number"
                              value={producto.cantidad}
                              onChange={(e) => {
                                const qty = parseInt(e.target.value, 10) || 0;
                                actualizarCantidad(producto.id, qty);
                                setFormDirty(true);
                              }}
                            />
                          </td>

                          {/* Precio */}
                          <td id="precio-col">
                            <input
                              type="text"
                              value={producto.tarifa?.toString() || ""}
                              onFocus={(e) => {
                                e.target.value =
                                  parseFloat(producto.tarifa) || "";
                              }}
                              onChange={(e) => {
                                const raw = e.target.value.replace(
                                  /[^0-9.]/g,
                                  ""
                                );
                                if (/^\d*\.?\d*$/.test(raw)) {
                                  const tarifa = parseFloat(raw) || 0;
                                  setDatos((prev) =>
                                    prev.map((item) =>
                                      item.id === producto.id
                                        ? {
                                            ...item,
                                            tarifa,
                                            importe: tarifa * item.cantidad,
                                          }
                                        : item
                                    )
                                  );
                                  setFormDirty(true);
                                }
                              }}
                              onBlur={(e) => {
                                const num = parseFloat(e.target.value);
                                if (!isNaN(num)) {
                                  setDatos((prev) =>
                                    prev.map((item) =>
                                      item.id === producto.id
                                        ? {
                                            ...item,
                                            tarifa: num,
                                            importe: num * item.cantidad,
                                          }
                                        : item
                                    )
                                  );
                                  e.target.value = num.toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  });
                                }
                              }}
                              style={{
                                width: "100%",
                                textAlign: "right",
                                padding: "8px",
                                fontSize: "14px",
                                borderRadius: "4px",
                              }}
                            />
                          </td>

                          {/* Importe */}
                          <td
                            id="importe-col"
                            style={{ textAlign: "right", paddingRight: "10px" }}
                          >
                            $
                            {producto.importe.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>

                          {/* Cant. instalada */}
                          <td>
                            <input
                              type="number"
                              value={producto.cantidadInstalada || ""}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10) || 0;
                                setDatos((prev) =>
                                  prev.map((item) =>
                                    item.id === producto.id
                                      ? { ...item, cantidadInstalada: val }
                                      : item
                                  )
                                );
                                setFormDirty(true);
                              }}
                              style={{ width: "100%", textAlign: "center" }}
                            />
                          </td>

                          {/* Avance % */}
                          <td>
                            <input
                              type="number"
                              value={producto.avance || ""}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                setDatos((prev) =>
                                  prev.map((item) =>
                                    item.id === producto.id
                                      ? { ...item, avance: val }
                                      : item
                                  )
                                );
                                setFormDirty(true);
                              }}
                              style={{ width: "100%", textAlign: "center" }}
                            />
                          </td>

                          {/* Imagen */}
                          <td>
                            {producto.imagenUrl ? (
                              <img
                                src={producto.imagenUrl}
                                alt="Producto"
                                style={{
                                  width: "100px",
                                  height: "100px",
                                  objectFit: "contain",
                                  margin: "0 auto",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              />
                            ) : (
                              <div style={{ textAlign: "center" }}>
                                <input
                                  type="file"
                                  id={`fileInput-${producto.id}`}
                                  style={{ display: "none" }}
                                  accept="image/*"
                                  onChange={(e) =>
                                    handleFileChange(e, producto.id)
                                  }
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    document
                                      .getElementById(
                                        `fileInput-${producto.id}`
                                      )
                                      .click()
                                  }
                                  style={{ fontSize: "0.8rem" }}
                                >
                                  Subir imagen
                                </button>
                              </div>
                            )}
                          </td>

                          {/* Botones de acción */}
                          <div
                            style={{
                              position: "absolute",
                              right: "-75px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              display: "flex",
                            }}
                          >
                            <button
                              type="button"
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-three-dots-vertical"
                                style={{ fontSize: "1.2rem" }}
                              />
                            </button>
                            <button
                              type="button"
                              style={{
                                background: "none",
                                border: "none",
                                color: "#dc3545",
                              }}
                              onClick={() => eliminar(producto.id)}
                            >
                              <i
                                className="bi bi-x-lg"
                                style={{ fontSize: "1.2rem" }}
                              />
                            </button>
                          </div>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="acciones-tabla">
              <button type="button" onClick={añadirFila}>
                <i
                  style={{ color: "#007bff", fontSize: "16px" }}
                  className="bi bi-plus-circle-fill"
                ></i>
                Añadir nueva fila
              </button>
              <button type="button" onClick={agregarBulk}>
                <i
                  style={{ color: "#007bff", fontSize: "16px" }}
                  className="bi bi-plus-circle-fill"
                ></i>
                Agregar artículos a granel
              </button>
            </div>

            <div className="data-container">
              <div className="notes-container">
                <label htmlFor="customer-notes">Notas</label>
                <textarea
                  id="customer-notes"
                  placeholder="Escribe tus notas aquí..."
                  value={notas}
                  onChange={(e) => {
                    setNotas(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                ></textarea>
              </div>

              <div className="totales-container">
                <div className="row">
                  <span className="label">Subtotal</span>
                  <span>
                    $
                    {calcularSubtotal().toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                <div className="row iva">
                  <span className="label">
                    IVA ({(obtenerIVA() * 100).toFixed(0)}%)
                  </span>
                  <span>
                    $
                    {iva.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                <hr />

                <div className="row total">
                  <span>Total ({divisa})</span>
                  <span className="total-amount">
                    $
                    {total.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#f9f9fb",
                padding: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "10px",
                width: "94%",
                marginTop: "30px",
                paddingBottom: "30px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              {/* Left Section: Términos y Condiciones */}
              <div className="terminos-container">
                <h4>CONDICIONES / OBSERVACIONES</h4>
                <textarea
                  value={terminos}
                  onChange={(e) => {
                    setTerminos(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                />
              </div>

              {/* Right Section: File Upload */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "8px",
                  flex: "0 0 530px",
                  marginTop: "30px",
                }}
              >
                <label style={{ fontSize: "14px" }}>
                  Adjuntar archivo(s) a Estimación
                </label>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    border: "1px dashed #ccc",
                    borderRadius: "5px",
                    overflow: "hidden", // Eliminar cualquier contenido que se salga del borde
                  }}
                >
                  <label
                    style={{
                      padding: "8px 10px",
                      backgroundColor: "#fff",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      whiteSpace: "nowrap", // Evitar que el contenido del botón se ajuste en varias líneas
                    }}
                  >
                    <i style={{ color: "#000" }} className="bi bi-upload"></i>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#000",
                        fontWeight: "lighter",
                      }}
                    >
                      Cargar archivo
                    </span>
                    <input
                      type="file"
                      style={{ display: "none" }} // Ocultar el input de archivo
                      accept=".csv,.xls,.xlsx,.doc,.docx,.pdf,.jpg,.jpeg,.png,.svg,.txt"
                      onChange={manejarArchivos}
                    />
                  </label>

                  <div
                    style={{
                      padding: "10px",
                      backgroundColor: "#fff",
                      borderLeft: "1px dashed #ccc", // Borde que conecta con el input
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      borderTopRightRadius: "auto", // Bordes redondeados en la esquina derecha
                      borderBottomRightRadius: "auto",
                      minWidth: "20px", // Evitar que el botón se haga demasiado pequeño
                      margin: "0", // Eliminar cualquier margen que agregue espacio extra
                    }}
                  >
                    <i
                      style={{
                        color: "#000",
                        fontSize: "14px", // Ajustar el tamaño del ícono
                      }}
                      className="bi bi-chevron-down"
                    ></i>
                  </div>
                </div>

                <p style={{ fontSize: "12px", color: "#888" }}>
                  Puedes cargar un máximo de 10 archivos, cada uno de 10 MB
                </p>

                {cargando && (
                  <p style={{ fontSize: "14px", color: "#888" }}>
                    Cargando archivo...
                  </p>
                )}

                {archivos.length > 0 && (
                  <div style={{ width: "100%" }}>
                    <strong>Archivos cargados:</strong>
                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        marginTop: "10px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {archivos.map((archivo) => {
                        const extension = archivo.nombre
                          .split(".")
                          .pop()
                          .toLowerCase();
                        const { icon, color } = obtenerIcono(extension);

                        return (
                          <li
                            key={archivo.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                              padding: "10px",
                              border: "1px solid #ddd",
                              borderRadius: "5px",
                              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                              backgroundColor: "#f9ffb",
                            }}
                          >
                            <i
                              className={`bi ${icon}`}
                              style={{
                                color,
                                fontSize: "1.5rem",
                                flexShrink: 0,
                              }}
                            />
                            <span style={{ flexGrow: 1 }}>
                              {archivo.nombre}
                            </span>

                            {/* Descargar */}
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                descargarArchivo(archivo.url, archivo.nombre);
                              }}
                              style={{
                                color: "#007bff",
                                background: "none",
                                border: "none",
                                fontWeight: "bold",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-download"
                                style={{ fontSize: "20px" }}
                              />
                            </button>

                            {/* Eliminar */}
                            <button
                              type="button"
                              onClick={() =>
                                eliminarArchivo(archivo.id, archivo.path)
                              }
                              style={{
                                border: "none",
                                background: "transparent",
                                color: "#dc3545",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-trash"
                                style={{ fontSize: "20px" }}
                              />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Grupo de botones: Guardar como borrador, Guardar y enviar, Cancelar */}
          <div className="cotizacion-toolbar">
            <div className="btn-group">
              <button
                id="guardar-enviar"
                className="btn btn-primary"
                type="button"
                onClick={guardar}
              >
                {modoEdicion ? "Actualizar" : "Guardar y enviar"}
              </button>

              <button
                id="cancelar"
                className="btn btn-secondary"
                type="button"
                onClick={handleCancel}
              >
                Cancelar
              </button>
            </div>
          </div>
        </form>

        {mostrarTabla && <Generador reportes={reportes} />}
      </div>

      <NumeroConfigModalUI />
    </>
  );
};

export default GeneradorModal;
